import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { useGetIncomingVehiclesQuery } from '../../api/compoundManagementApi';
import { handleQueryError } from '../../notifications/ErrorNotification';
import {
    getCompoundManagementModalFilter,
    getDebouncedVinSearchFilter,
    getIncomingVehicleSorting,
    mapIncomingVehiclesFilterQueryParams,
} from '../../reducers/compoundManagementSlice';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { CompoundManagementToolbar } from '../shared/toolbar/CompoundManagementToolbar';
import { StartBulkStatusUpdateButton } from '../shared/toolbar/StartBulkStatusUpdateButton';
import { IncomingVehiclesTable } from './table/IncomingVehiclesTable';
import { incomingVehiclesColumnDefinition } from './table/IncomingVehiclesTableColumn';

export const DEFAULT_COMPOUND_INCOMING_LIMIT_SIZE = 100;
export const NEXT_COMPOUND_INCOMING_LIMIT_SIZE = 1000;
const POLLING_INTERVAL = 60000;

export const IncomingVehicles: FunctionComponent = () => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const debouncedVinSearch = useAppSelector(getDebouncedVinSearchFilter);
    const filters = useAppSelector(getCompoundManagementModalFilter);
    const sorting = useAppSelector(getIncomingVehicleSorting);
    const [limit, setLimit] = useState(DEFAULT_COMPOUND_INCOMING_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_COMPOUND_INCOMING_LIMIT_SIZE;

    const {
        data: incomingPage,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetIncomingVehiclesQuery(
        {
            ...mapIncomingVehiclesFilterQueryParams(filters, debouncedVinSearch),
            limit,
            sortBy: sorting,
        },
        {
            pollingInterval: isSessionExpired ? undefined : POLLING_INTERVAL,
        },
    );

    useSetUserTriggeredFetching({
        isFetching,
        depsWhichTriggerUserFetching: [sorting, filters, debouncedVinSearch],
    });

    if (isError) {
        console.error(error);
        handleQueryError(error);
    }

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (incomingPage === undefined) {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const incomingVehicles = incomingPage.items;
    return (
        <TableColumnSettingsState tableId="compound.incoming" columnDefinition={incomingVehiclesColumnDefinition}>
            <CompoundManagementToolbar bulkActionButton={<StartBulkStatusUpdateButton />} />
            <IncomingVehiclesTable vehicles={incomingVehicles} />
            {incomingVehicles.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={incomingPage.hasMore}
                    limitReached={nextLimit > NEXT_COMPOUND_INCOMING_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};
